<template>
    <div
        class="route-simulation-view"
    >
        <leaflet-map
            ref="leafletMap"
            :center="mapCenter"
            :menu-items
            class="simulation-map"
            @map-ready="onMapReady"
            @on-map-clicked="handleMapClick"
        />

        <!-- Vehicle Details Panel -->
        <div
            v-if="selectedVehicle && selectedVehicle.metadata"
            class="vehicle-panel"
        >
            <div class="panel-header">
                <h4>{{ selectedVehicle.label || selectedVehicle.properties.name }}</h4>
                <span
                    v-if="operationType"
                    class="panel-operation-type"
                >{{ $t('route_optimization.' + operationType) }}</span>
            </div>
            <div class="legend-content">
                <table class="table table-borderless">
                    <thead>
                        <tr>
                            <th v-if="selectedVehicle.metadata.total_length">
                                {{ $t('route_optimization.total_length') }}
                            </th>
                            <th v-if="selectedVehicle.metadata.working_length">
                                {{ $t('route_optimization.working_length') }}
                            </th>
                            <th v-if="selectedVehicle.metadata.total_length && selectedVehicle.metadata.working_length">
                                {{ $t('route_optimization.travel_length') }}
                            </th>
                            <th v-if="selectedVehicle.metadata.total_duration">
                                {{ $t('route_optimization.duration') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td v-if="selectedVehicle.metadata.total_length">
                                {{ (selectedVehicle.metadata.total_length / 1000).toFixed(2) }} km
                            </td>
                            <td v-if="selectedVehicle.metadata.working_length">
                                {{ (selectedVehicle.metadata.working_length / 1000).toFixed(2) }} km
                            </td>
                            <td v-if="selectedVehicle.metadata.total_length && selectedVehicle.metadata.working_length">
                                {{ ((selectedVehicle.metadata.total_length - selectedVehicle.metadata.working_length) / 1000).toFixed(2) }} km
                            </td>
                            <td v-if="selectedVehicle.metadata.total_duration">
                                {{ formatDuration(selectedVehicle.metadata.total_duration) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-if="!selectedVehicle && simulationMetaData.total_length"
            class="vehicle-panel"
        >
            <div class="panel-header">
                <h4>{{ legendTypes.length > 1 ? $t('route_optimization.all_vehicles') : legendTypes[0]?.label }}</h4>
                <span
                    v-if="operationType"
                    class="panel-operation-type"
                >{{ $t('route_optimization.' + operationType) }}</span>
            </div>
            <div class="legend-content">
                <table class="table table-borderless">
                    <thead>
                        <tr>
                            <th>{{ $t('route_optimization.total_length') }}</th>
                            <th>{{ $t('route_optimization.working_length') }}</th>
                            <th>{{ $t('route_optimization.travel_length') }}</th>
                            <th>{{ $t('route_optimization.duration') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div>{{ (simulationMetaData.total_length / 1000).toFixed(2) }} km</div>
                                <div
                                    v-if="simulationMetaData.min_total_length"
                                    class="range-text"
                                >
                                    {{ (simulationMetaData.min_total_length / 1000).toFixed(2) }} km - {{ (simulationMetaData.max_total_length / 1000).toFixed(2) }} km
                                </div>
                            </td>
                            <td>
                                <div>{{ (simulationMetaData.working_length / 1000).toFixed(2) }} km</div>
                                <div
                                    v-if="simulationMetaData.min_working_length"
                                    class="range-text"
                                >
                                    {{ (simulationMetaData.min_working_length / 1000).toFixed(2) }} km - {{ (simulationMetaData.max_working_length / 1000).toFixed(2) }} km
                                </div>
                            </td>
                            <td>
                                <div>{{ ((simulationMetaData.total_length - simulationMetaData.working_length) / 1000).toFixed(2) }} km</div>
                                <div
                                    v-if="simulationMetaData.min_travel_length"
                                    class="range-text"
                                >
                                    {{ (simulationMetaData.min_travel_length / 1000).toFixed(2) }} km - {{ (simulationMetaData.max_travel_length / 1000).toFixed(2) }} km
                                </div>
                            </td>
                            <td>
                                <div>{{ formatDuration(simulationMetaData.total_duration) }}</div>
                                <div
                                    v-if="simulationMetaData.min_duration"
                                    class="range-text"
                                >
                                    {{ formatDuration(simulationMetaData.min_duration) }} - {{ formatDuration(simulationMetaData.max_duration) }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Legend Panel -->
        <div class="legend-panel">
            <div class="panel-header">
                <h4>{{ $t("route_optimization.legend") }}</h4>
            </div>
            <div class="legend-content">
                <div
                    v-if="legendTypes.length > 1"
                    class="legend-item"
                    :class="{ 'selected': !selectedVehicle }"
                    @click="selectVehicle(null)"
                >
                    <div
                        class="legend-color"
                        style="background: #666"
                    />
                    <span class="legend-label">{{ $t('route_optimization.all_vehicles') }}</span>
                </div>
                <template
                    v-for="vehicle in legendTypes"
                    :key="vehicle.id"
                >
                    <div
                        class="legend-item"
                        :class="{ 'selected': selectedVehicle?.index === vehicle.index }"
                        @click="selectVehicle(vehicle.index)"
                    >
                        <div
                            class="legend-color"
                            :style="{ background: vehicle.color }"
                        />
                        <span class="legend-label">{{ vehicle.label }}</span>
                    </div>
                    <!-- Sub-legends: shown directly under the selected vehicle row -->
                    <template
                        v-if="selectedVehicle?.index === vehicle.index"
                    >
                        <div
                            class="legend-item legend-item--sub"
                            :class="{ 'selected': !hiddenLayers[vehicle.index + '-work'] }"
                            @click.stop="toggleLayerVisibility(vehicle.index, 'work')"
                        >
                            <div
                                class="legend-color legend-color--sub"
                                :style="{ background: vehicle.color }"
                            />
                            <span class="legend-label legend-label--sub">{{ $t('route_optimization.work') }}</span>
                        </div>
                        <div
                            class="legend-item legend-item--sub"
                            :class="{ 'selected': !hiddenLayers[vehicle.index + '-resettle'] }"
                            @click.stop="toggleLayerVisibility(vehicle.index, 'resettle')"
                        >
                            <div
                                class="legend-color legend-color--sub"
                                style="background: #6c757d"
                            />
                            <span class="legend-label legend-label--sub">{{ $t('route_optimization.resettle') }}</span>
                        </div>
                    </template>
                </template>

                <hr class="legend-divider">

                <div
                    v-if="originalRouteGeometry"
                    class="legend-item"
                    :class="{ 'selected': showOriginalRoute }"
                    @click="showOriginalRoute = !showOriginalRoute"
                >
                    <div
                        class="legend-color legend-color--dashed"
                        style="background: #2196F3"
                    />
                    <span class="legend-label">{{ $t('route_optimization.original_route') }}</span>
                </div>
            </div>
        </div>

        <!-- Player Controls -->
        <div class="player-controls">
            <!-- Timeline Scrubber -->
            <div class="timeline-container">
                <input
                    :value="currentTimelinePosition"
                    type="range"
                    min="0"
                    :max="maxTimelinePosition"
                    step="1"
                    class="timeline-scrubber"
                    @input="scrubToPosition($event.target.value)"
                >
                <div class="timeline-labels">
                    <span>{{ currentTimeString }}</span>
                    <span>{{ totalTimeString }}</span>
                </div>
            </div>

            <div class="control-row">
                <div class="control-buttons">
                    <button
                        class="control-btn"
                        :class="{ active: playback.playing }"
                        @click="togglePlayback"
                    >
                        <font-awesome-icon
                            v-if="!playback.playing"
                            icon="play"
                        />
                        <font-awesome-icon
                            v-else
                            icon="pause"
                        />
                    </button>

                    <button
                        class="control-btn"
                        @click="stepBack"
                    >
                        <font-awesome-icon
                            icon="chevron-left"
                        />
                    </button>

                    <button
                        class="control-btn"
                        @click="stepForward"
                    >
                        <font-awesome-icon
                            icon="chevron-right"
                        />
                    </button>
                </div>

                <div class="speed-control">
                    <label>{{ $t("route_optimization.speed") }}</label>
                    <input
                        v-model.number="playback.speed"
                        type="range"
                        min="1"
                        max="30"
                        step="0.2"
                        class="speed-slider"
                    >
                    <span class="speed-value">{{ playback.speed.toFixed(1) }}x</span>
                </div>

                <div class="loop-control">
                    <label>
                        <input
                            v-model="playback.loop"
                            type="checkbox"
                        >
                        {{ $t("route_optimization.loop") }}
                    </label>
                </div>
            </div>
        </div>

        <!-- Back Button -->
        <div class="back-controls">
            <button
                class="btn btn-secondary"
                @click="$emit('go-back')"
            >
                ← {{ useStore ? $t("route_optimization.back_to_setup") : $t("common.close") }}
            </button>
        </div>

        <!-- Google Stree View -->
        <google-street-view-link
            v-if="streetViewDialogCoordinates"
            ref="streetView"
            :lat-lon="streetViewDialogCoordinates"
            :hide-text="true"
            @dialog-closed="streetViewDialogCoordinates = null"
        />
    </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { routeOptimizationMixin } from '../mixins/RouteOptimizationMixin';
import { restApi } from '../mixins/RestApiMixin';
import LeafletMap from "@/components/map/LeafletMap.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import GoogleStreetViewLink from "../utils/GoogleStreetViewLink.vue";


export default {
    name: "RouteOptimizationSimulation",
    components: {
        GoogleStreetViewLink,
        LeafletMap,
    },
    mixins: [routeOptimizationMixin, restApi],
    props: {
        useStore: {
            type: Boolean,
            default: true
        },
        routeData: {
            type: Array,
            default: null
        }
    },
    emits: ['go-back'],
    data() {
        return {
            playback: {
                playing: false,
                speed: 1,
                loop: true
            },
            mapCenter: {
                lat: 62.2426,
                lon: 25.7482
            },
            tourLayer: null,
            layersByType: {},
            stepPolylines: [],
            vehicles: [],
            map: null,
            mapSelectionMode: false,
            selectedVehicle: null,
            manualTimelinePosition: 0,
            hiddenLayers: {},
            showOriginalRoute: false,
            streetViewDialogCoordinates: null,
            menuItems: [
                {
                    text: "Google Street View",
                    onClick: this.showStreetView
                }
            ],
        };
    },

    computed: {
        routeOptimizationResult() {
            if (!this.useStore && this.routeData) {
                return this.routeData
            }
            return this.$store.state.routeOptimization.routeOptimizationResult;
        },
        originalRouteGeometry() {
            if (!this.useStore) return null
            return this.$store.state.routeOptimization.originalRouteGeometry;
        },
        operationType() {
            if (!this.useStore) return null
            return this.$store.state.routeOptimization.operationType;
        },
        legendTypes() {
            const simArray = this.routeOptimizationResult;

            if (!simArray || simArray.length === 0) return [];

            if (!this.useStore) {
                return simArray.map((vehicle, index) => ({
                    id: `vehicle-${index}`,
                    index: index,
                    label: `${this.$t('route_optimization.vehicle')} ${index + 1}`,
                    color: vehicle.metadata?.vehicleColor || '#7FC000',
                    metadata: vehicle.metadata
                }));
            }

            const usedVehicles = this.$store.state.routeOptimization.usedVehicles;

            return simArray.map((vehicle, index) => {
                const usedVehicleDetails = usedVehicles[index]?.properties || {};

                return {
                    id: `vehicle-${index}`,
                    index: index,
                    label: usedVehicleDetails.name || `${this.$t('route_optimization.vehicle')} ${index + 1}`,
                    color: vehicle.metadata?.vehicleColor || '#7FC000',
                    metadata: vehicle.metadata
                };
            });
        },
        simulationMetaData() {
            const optimizedRoutes = this.routeOptimizationResult;
            if (!optimizedRoutes || optimizedRoutes.length === 0) return {};

            const totals = optimizedRoutes.reduce((acc, route) => {
                const meta = route.metadata || {};
                acc.total_duration = (acc.total_duration || 0) + (meta.total_duration || 0);
                acc.total_length = (acc.total_length || 0) + (meta.total_length || 0);
                acc.working_length = (acc.working_length || 0) + (meta.working_length || 0);
                return acc;
            }, {});

            // Calculate min/max ranges if multiple vehicles
            if (optimizedRoutes.length > 1) {
                const durations = optimizedRoutes.map(r => r.metadata?.total_duration || 0).filter(d => d > 0);
                const totalLengths = optimizedRoutes.map(r => r.metadata?.total_length || 0).filter(l => l > 0);
                const workingLengths = optimizedRoutes.map(r => r.metadata?.working_length || 0).filter(l => l > 0);
                const travelLengths = optimizedRoutes.map(r => {
                    const total = r.metadata?.total_length || 0;
                    const working = r.metadata?.working_length || 0;
                    return total - working;
                }).filter(l => l > 0);

                if (durations.length > 0) {
                    totals.min_duration = Math.min(...durations);
                    totals.max_duration = Math.max(...durations);
                }
                if (totalLengths.length > 0) {
                    totals.min_total_length = Math.min(...totalLengths);
                    totals.max_total_length = Math.max(...totalLengths);
                }
                if (workingLengths.length > 0) {
                    totals.min_working_length = Math.min(...workingLengths);
                    totals.max_working_length = Math.max(...workingLengths);
                }
                if (travelLengths.length > 0) {
                    totals.min_travel_length = Math.min(...travelLengths);
                    totals.max_travel_length = Math.max(...travelLengths);
                }
            }

            return totals;
        },
        maxTimelinePosition() {
            if (!this.vehicles.length) return 100;
            // Maximum total simulation-seconds across all vehicles (the longest route)
            return Math.max(...this.vehicles.map(v => v.totalDurationSec || 0));
        },
        currentTimelinePosition: {
            get() {
                return this.manualTimelinePosition;
            },
            set(value) {
                this.manualTimelinePosition = value;
            }
        },
        currentTimeString() {
            return this.formatTimelinePosition(this.currentTimelinePosition);
        },
        totalTimeString() {
            return this.formatTimelinePosition(this.maxTimelinePosition);
        }
    },

    watch: {
        routeOptimizationResult(newResult) {
            if (!this.useStore) return;
            if (!newResult || newResult.length === 0) {
                this.clearSimulation();
            } else {
                this.loadSimulationFromStore();
                this.$nextTick(() => this.autoSelectSingleVehicle());
            }
        },
        showOriginalRoute(val) {
            if (!this.map || !this.originalRouteLayer) return
            this.originalRouteLayer.clearLayers()
            if (val && this.originalRouteGeometry?.features) {
                this.originalRouteGeometry.features.forEach(feature => {
                    if (feature.geometry?.type === 'LineString') {
                        const latlngs = feature.geometry.coordinates.map(c => [c[1], c[0]])
                        L.polyline(latlngs, { color: '#2196F3', weight: 8, opacity: 0.5 }).addTo(this.originalRouteLayer)
                    } else if (feature.geometry?.type === 'MultiLineString') {
                        feature.geometry.coordinates.forEach(line => {
                            const latlngs = line.map(c => [c[1], c[0]])
                            L.polyline(latlngs, { color: '#2196F3', weight: 8, opacity: 0.5 }).addTo(this.originalRouteLayer)
                        })
                    }
                })
                // Work routes must stay on top after the original route overlay is added
                this.bringWorkLayersToFront()
                this.traceLayer?.eachLayer(l => l.bringToFront())
            }
        }
    },

    methods: {
        onMapReady(map) {
            this.map = map || this.$refs.leafletMap?.map;
            if (this.map) {
                this.originalRouteLayer = L.layerGroup().addTo(this.map);
                this.tourLayer = L.layerGroup().addTo(this.map);
                this.highlightLayer = L.layerGroup().addTo(this.map);
                this.traceLayer = L.layerGroup().addTo(this.map);
                this.map.on('click', this.handleMapClick);

                if (!this.useStore && this.routeData && this.routeData.length > 0) {
                    this.loadSimulationFromProps();
                } else {
                    this.loadSimulationFromStore();
                }
                this.$nextTick(() => this.autoSelectSingleVehicle());
            }
        },
        enableMapSelection() {
            this.mapSelectionMode = true;
        },
        handleMapClick(latlng) {
            if (this.mapSelectionMode) {
                if (this.$refs.routeOptimizationPanel) {
                    this.$refs.routeOptimizationPanel.handleMapLocationSelected({ lat: latlng.lat, lng: latlng.lng });
                }
                setTimeout(() => {
                    this.mapSelectionMode = false;
                }, 100);
            }
        },
        loadSimulationFromProps() {
            if (!this.routeData || this.routeData.length === 0) return;

            this.clearSimulation();
            this.vehicles = [];

            this.routeData.forEach((simObj, vehicleIndex) => {
                const vehicleData = this.initializeSimulation(simObj, vehicleIndex);
                this.vehicles.push(vehicleData);
            });

            this.fitMapToTour();
            this.bringWorkLayersToFront();
            this.startPlayback();
        },
        autoSelectSingleVehicle() {
            if (this.legendTypes.length === 1) {
                this.selectVehicle(0);
            }
        },

        togglePlayback() {
            if (this.playback.playing) {
                this.pausePlayback();
            } else {
                this.startPlayback();
            }
        },
        selectVehicle(vehicleIndex) {
            if (vehicleIndex === null) {
                this.selectedVehicle = null;
                this.hiddenLayers = {};
                this.clearSegmentHighlight();
                this.highlightVehicle(null);
                return;
            }

            if (this.selectedVehicle?.index === vehicleIndex) {
                this.selectedVehicle = null;
                this.hiddenLayers = {};
                this.clearSegmentHighlight();
                this.highlightVehicle(null);
            } else {
                this.hiddenLayers = {};
                this.clearSegmentHighlight();

                const vehicle = this.legendTypes.find(v => v.index === vehicleIndex);
                const simObjs = this.routeOptimizationResult;
                const usedVehicles = this.useStore ? this.$store.state.routeOptimization.usedVehicles : [];
                const simObj = simObjs?.[vehicleIndex];
                const usedVehicle = usedVehicles[vehicleIndex];

                this.selectedVehicle = {
                    ...vehicle,
                    properties: usedVehicle?.properties || {},
                    metadata: {
                        ...simObj?.metadata,
                        ...usedVehicle?.properties
                    }
                };
                this.highlightVehicle(vehicleIndex);
            }
        },
        formatDuration(durationSeconds) {
            if (durationSeconds == null || Number.isNaN(durationSeconds)) {
                return '-';
            }

            const totalSeconds = Math.max(0, Math.round(durationSeconds));
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);

            return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
        },
        clearSimulation() {
            this.selectedVehicle = null;
            this.playback.playing = false;
            this.showOriginalRoute = false;
            this.hiddenLayers = {};
            this.clearSegmentHighlight();

            if (this.tourLayer) {
                this.tourLayer.clearLayers();
            }
            if (this.traceLayer) {
                this.traceLayer.clearLayers();
            }
            if (this.originalRouteLayer) {
                this.originalRouteLayer.clearLayers();
            }

            this.stepPolylines = [];
            this.vehicles = [];
        },
        toggleLayerVisibility(vehicleIndex, type) {
            const key = `${vehicleIndex}-${type}`;
            const isHidden = !!this.hiddenLayers[key];
            this.hiddenLayers = { ...this.hiddenLayers, [key]: !isHidden };

            this.stepPolylines.forEach(o => {
                if (o.vehicleIndex !== vehicleIndex || o.type !== type) return;
                if (!isHidden) {
                    // Hiding: remove from map entirely so it can't receive clicks
                    this.tourLayer.removeLayer(o.layer);
                } else {
                    // Showing: add back and restore style
                    o.layer.setStyle({ opacity: 0.95, fillOpacity: 0.95 });
                    if (!this.tourLayer.hasLayer(o.layer)) {
                        o.layer.addTo(this.tourLayer);
                    }
                    this.bringWorkLayersToFront();
                }
            });
        },

        scrubToPosition(position) {
            this.playback.playing = false;
            this.seekToSeconds(parseFloat(position));
        },
        formatTimelinePosition(seconds) {
            const totalSec = Math.max(0, Math.round(seconds));
            const hours = Math.floor(totalSec / 3600);
            const minutes = Math.floor((totalSec % 3600) / 60);
            const secs = totalSec % 60;
            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
            return `${minutes}:${secs.toString().padStart(2, '0')}`;
        },
        showStreetView(coord) {
            if (coord) {
                this.streetViewDialogCoordinates = {lat: coord.lat, lon: coord.lng}
                this.$nextTick(() => this.$refs.streetView.showPrompt())
            }
        }
    }
};
</script>


<style scoped>
.route-simulation-view {
    position: relative;
    height: 100vh;
    background: #f5f5f5;
}

.simulation-map {
    width: 100%;
    height: 100%;
}

.vehicle-panel {
    position: absolute;
    top: 20px;
    left: 210px;
    right: 240px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    font-size: 0.75rem;
    color: #666;
}

.legend-panel {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 200px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
}

.panel-header {
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    background: #f8f9fa;
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.panel-header h4 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
}

.panel-operation-type {
    font-size: 0.9rem;
    font-weight: 600;
    color: #7FC000;
    white-space: nowrap;
}

.vehicle-panel :deep(.table th) {
    color: #7FC000 !important;
}

.legend-content {
    padding: 12px;
}

.vehicle-panel .table {
    width: 100%;
    margin: 0;
    font-size: 0.85rem;
}

.vehicle-panel .table th {
    text-align: center;
    font-weight: 600;
    padding: 8px 4px;
}

.vehicle-panel .table td {
    text-align: center;
    padding: 8px 4px;
}

.range-text {
    font-size: 0.75rem;
    color: #999;
    margin-top: 2px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px 8px 14px;
    border-radius: 0;
    cursor: pointer;
    transition: background-color 0.2s;
    border-left: 3px solid transparent;
}

.legend-item:hover {
    background: #f8f9fa;
}

.legend-item.selected {
    background: #f8f9fa;
    border-left: 3px solid #7FC000;
}

.legend-item--sub {
    padding-left: 28px;
    border-left: 3px solid #ddd;
    opacity: 0.5;
}

.legend-item--sub:hover {
    background: #f8f9fa;
    opacity: 1;
}

.legend-item--sub.selected {
    border-left: 3px solid #7FC000;
    background: #f8f9fa;
    opacity: 1;
}

.legend-item--sub.selected:hover {
    background: #f8f9fa;
}

.legend-color {
    width: 18px;
    height: 12px;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) inset;
}

.legend-color--sub {
    width: 15px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
    box-shadow: none;
}

.legend-color--dashed {
    background: #2196F3 !important;
    height: 4px;
    margin-top: 4px;
    box-shadow: none;
    border-top: none;
}

.legend-divider {
    border: none;
    border-top: 1px solid #eee;
    margin: 6px 0;
}

.legend-label {
    font-size: 0.85rem;
    text-transform: capitalize;
    color: #555;
}

.legend-label--sub {
    font-size: 0.82rem;
    color: #555;
}

.player-controls {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 1000;
    min-width: 400px;
}

.timeline-container {
    width: 100%;
}

.timeline-scrubber {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: #e9ecef;
    outline: none;
    cursor: pointer;
}

.timeline-scrubber::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.timeline-scrubber::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #007bff;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.timeline-labels {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: #666;
    margin-top: 4px;
}

.control-row {
    display: flex;
    align-items: center;
    gap: 20px;
}

.control-buttons {
    display: flex;
    gap: 8px;
}

.control-btn {
    min-width: 40px;
    height: 40px;
    border: none;
    border-radius: 8px;
    background: #f8f9fa;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    font-size: 0.8rem;
    padding: 0 12px;
}

.control-btn:hover {
    background: #e9ecef;
}

.control-btn.active {
    background: #007bff;
    color: white;
}

.speed-control {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
}

.speed-slider {
    width: 120px;
}

.speed-value {
    min-width: 40px;
    text-align: right;
    font-weight: 500;
}

.loop-control {
    font-size: 0.85rem;
}

.loop-control label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
}

.back-controls {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 1000;
}

@media (max-width: 768px) {
    .legend-panel {
        width: 160px;
    }

    .player-controls {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }

    .speed-control .speed-slider {
        width: 100px;
    }
}
</style>
